/* Live read of the one document a household IS.

   useOrgSubscription already derives status/daysLeft from the same doc, but a
   family surface also needs the plain facts on it: who the admin is (for the
   checkout email), when the free days actually end (pilotEndsAt, which is one
   day BEFORE expirationDate by design), and whether a cancellation is already
   queued. Reading it once here keeps every family screen agreeing. */
import { useEffect, useState } from 'react';
import { collection, doc, limit, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase';

export interface FamilyOrg {
  loading: boolean;
  name: string;
  adminEmail: string;
  /** Last free day of the pilot — what we promise on screen. */
  pilotEndsAt: Date | null;
  /** The day billing would actually run (pilotEndsAt + 1). */
  firstChargeAt: Date | null;
  isPilot: boolean;
  plan: string;
  cadence: string;
  subscriptionStatus: string;
  /** Cancellation queued: access runs to the end of the paid period, then stops. */
  cancelAtPeriodEnd: boolean;
  /** familySettings.driverMode — 'private_driver' | 'parent_drives'. */
  driverMode: string;
  vehiclePlate: string;
  schoolName: string;
  analyticsAddon: boolean;
}

const EMPTY: FamilyOrg = {
  loading: true, name: '', adminEmail: '', pilotEndsAt: null, firstChargeAt: null,
  isPilot: false, plan: '', cadence: 'monthly', subscriptionStatus: '', cancelAtPeriodEnd: false,
  driverMode: '', vehiclePlate: '', schoolName: '', analyticsAddon: false,
};

function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') return (v as { toDate: () => Date }).toDate();
  if (v instanceof Date) return v;
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function useFamilyOrg(orgCode: string | null): FamilyOrg {
  const [org, setOrg] = useState<FamilyOrg>(EMPTY);

  useEffect(() => {
    if (!orgCode) {
      setOrg({ ...EMPTY, loading: false });
      return;
    }
    const q = query(collection(db, 'organizations'), where('code', '==', orgCode), limit(1));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const d = snap.docs[0]?.data() as Record<string, unknown> | undefined;
        if (!d) { setOrg({ ...EMPTY, loading: false }); return; }
        const fs = (d.familySettings || {}) as Record<string, unknown>;
        setOrg({
          loading: false,
          name: String(d.name || orgCode),
          adminEmail: String(d.adminEmail || d.email || '').trim().toLowerCase(),
          pilotEndsAt: toDate(d.pilotEndsAt),
          firstChargeAt: toDate(d.firstChargeAt) || toDate(d.expirationDate),
          isPilot: !!d.isPilot,
          plan: String(d.subscriptionPlan || d.plan || ''),
          cadence: String(d.billingCadence || 'monthly'),
          subscriptionStatus: String(d.subscriptionStatus || ''),
          cancelAtPeriodEnd: d.cancelAtPeriodEnd === true,
          driverMode: String(fs.driverMode || ''),
          vehiclePlate: String(fs.vehiclePlate || ''),
          schoolName: String(fs.schoolName || ''),
          analyticsAddon: d.analyticsAddon === true,
        });
      },
      () => setOrg({ ...EMPTY, loading: false }),
    );
    return () => unsub();
  }, [orgCode]);

  return org;
}

/** Whole days from now until `d`, floored at 0. */
export function daysUntil(d: Date | null): number {
  if (!d) return 0;
  return Math.max(0, Math.ceil((d.getTime() - Date.now()) / 86400000));
}

export function fmtDay(d: Date | null): string {
  if (!d) return '—';
  return d.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
}

/* The household's one vehicle id, which is also its number plate.
   It lives on familySchedules (written at activation, and re-pointed when a
   driver is invited), not on the org — so any surface that reads RUNS needs
   this before it can query. Kept here so Analytics, Reports and Settings all
   resolve it the same way. */
export function useFamilyBusId(orgCode: string | null): { busId: string; loading: boolean } {
  const [state, setState] = useState<{ busId: string; loading: boolean }>({ busId: '', loading: true });

  useEffect(() => {
    if (!orgCode) { setState({ busId: '', loading: false }); return; }
    const unsub = onSnapshot(
      doc(db, 'familySchedules', orgCode),
      (snap) => setState({ busId: String(snap.data()?.busId || ''), loading: false }),
      () => setState({ busId: '', loading: false }),
    );
    return () => unsub();
  }, [orgCode]);

  return state;
}
